import { ReducerState, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  MNIDashboard,
  MNIDashboardWidgetPreferences,
  MyMNIPreferences,
  preferencesApi,
  localPreferencesService,
  PreferenceConstants,
} from '@/core/preferences/export';
import { Context, AppContext } from '@/core/app-context/export';

import { faPlay, faRightLeft, faSearch, faTowerCell } from '@fortawesome/free-solid-svg-icons';

import { Layouts } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { useSearchParams } from 'react-router-dom';
import DashboardDeleteModal from '@/app/dashboard/components/dashboard-delete-modal';
import DashboardCreateModal from '@/app/dashboard/components/dashboard-create-modal';
import DashboardContent from '@/app/dashboard/components/dashboard-content';
import { shareApi } from '@/shared/share/export';
import { authService, useSessionId } from '@/core/auth/export';
import { themeService, isAlphaFlashHost } from '@/shared/theme/export';
import { WidgetHostContext, WidgetHost, useWidgetNavigatorRegistry } from '@/core/widgets/export';
import { WidgetNavigationProvider } from '@/core/widget-navigation/export';
import { registerInlineDashboardWidgetHandler } from '@/core/deep-dive/export';
import MobileDashboardShell from '@/app/dashboard/components/mobile/mobile-dashboard-shell';
import useWidgetsFromLayouts from '@/app/dashboard/hooks/use-widgets-from-layouts';
import {
  DashboardAction,
  DashboardContentReducer,
  dashboardContentReducerWrapper,
  DashboardState,
} from '@/app/dashboard/store/dashboard-content-reducer';
import useDashboardPersistence from '@/app/dashboard/hooks/use-dashboard-persistence';
import { useSubscription, MessageServiceConstants } from '@/core/messaging/export';
import {
  WidgetNotification,
  useWidgetNotifications,
  PushEnablePrompt,
} from '@/core/notifications/export';
import { RequirePermissions, Permissions } from '@/core/permissions/export';
import { useAuth0 } from '@auth0/auth0-react';
import DashboardCopyModal from './dashboard-copy-modal';
import { toast } from 'react-toastify';
import './dashboard.css';
import { RemoveWhenHiddenDiv } from '@/shared/components/export';
import { getBreakpointFromElement } from '../util/dashboard-breakpoints';
import Navbar from './navbar';
import { SearchProvider, SearchResults } from '@/core/search/export';
import DashboardAddWidgetModal from '@/app/dashboard/components/dashboard-add-widget-modal';
import DashboardAddWidgetSelectDashboardModal from './dashboard-add-widget-select-dashboard-modal';
import DashboardMarkAsFavoriteModal from '@/app/dashboard/components/dashboard-mark-as-favorite-modal';
import { ChatDrawer } from '@/core/chat/export';

export default function Dashboard() {
  const [query, setQuery] = useSearchParams();
  const [initialized, setInitialized] = useState(false);
  const [showDeleteDashboardModal, setShowDeleteDashboardModal] = useState(false);
  const [showMarkAsFavoriteModal, setShowMarkAsFavoriteModal] = useState(false);
  const [showCreateDashboardModal, setShowCreateDashboardModal] = useState(false);
  const [showCopyDashboardModal, setShowCopyDashboardModal] = useState(false);
  const [showAddWidgetModal, setShowAddWidgetModal] = useState(false);
  const [showAddWidgetSelectDashboardModal, setShowAddWidgetSelectDashboardModal] = useState(false);
  const [addWidgetWidgetId, setAddWidgetWidgetId] = useState<string>();
  const [resolveAddWidgetPromise, setResolveAddWidgetPromise] = useState<
    ((value: boolean) => void) | null
  >(null);
  const [copyDashboard, setCopyDashboard] = useState<MNIDashboard>();
  const [selectedDashboard, setSelectedDashboard] = useState<MNIDashboard>();
  const context = useContext<AppContext>(Context);

  const { getAccessTokenSilently } = useAuth0();

  const { notificationSoundOn, setNotificationSoundOn } = useWidgetNotifications();
  const activeWidgets = useWidgetsFromLayouts(selectedDashboard);

  const sessionId = useSessionId() ?? '';

  const [state, dispatch]: [s: DashboardState, d: (a: DashboardAction) => void] =
    useReducer<DashboardContentReducer>(dashboardContentReducerWrapper, {
      lastLayout: {} as Layouts,
      loading: true,
      editing: false,
      dirty: false,
      highlightedWidgets: {},
      dashboards: [],
    } as ReducerState<DashboardContentReducer>);

  useDashboardPersistence({ state, dispatch });

  useEffect(() => {
    setSelectedDashboard(state.dashboards.find(value => value.id === state.selectedDashboard));
  }, [state.selectedDashboard, state.dashboards]);

  useEffect(() => {
    if (state.loading) return;

    const widgetId = query.get('widget');

    if (widgetId) {
      setAddWidgetWidgetId(widgetId);
      setShowAddWidgetSelectDashboardModal(true);

      query.delete('widget');
      setQuery(query);
    }

    const share = query.get('share');

    if (share) {
      try {
        // Decode the share string
        const shareId = share;

        // Parse the JSON string back to an object
        shareApi.getShareValue<MNIDashboard>(shareId).then(
          dashboard => {
            if (!dashboard) {
              toast.error('Share link is not valid');
              return;
            }

            if (dashboard.name && dashboard.layouts) {
              setCopyDashboard(dashboard);
              setShowCopyDashboardModal(true);
            }
          },
          () => {
            toast.error('Share link expired!');
          },
        );
      } catch (e) {
        toast.error('Share link expired!');
      } finally {
        query.delete('share');
        setQuery(query);
      }
    }
    // eslint-disable-next-line
  }, [query, state]);

  useEffect(() => {
    if (initialized) return;

    //refresh token (auth0) when initial load
    authService.loginWithAuth0(getAccessTokenSilently);

    localPreferencesService.getLocalPreferences().then(value => {
      themeService.setThemeBasedOnUserPreferences(value);
    });

    preferencesApi.getPreferences<MyMNIPreferences>(PreferenceConstants.MY_MNI).then(val => {
      dispatch({
        type: 'dashboardsLoaded',
        dashboards: val?.dashboard || [],
      });
    });

    setInitialized(true);

    return () => {
      localPreferencesService.getLocalPreferences().then((value: any) => {
        themeService.setThemeBasedOnUserPreferences(value);
      });
    };
    // eslint-disable-next-line
  }, []);

  useSubscription<WidgetNotification>(
    MessageServiceConstants.WIDGET_NOTIFICATION_ACKNOWLEDGED,
    message => {
      message.widgetIds.forEach(value => {
        dispatch({ type: 'highlightWidget', id: value });
      });

      setTimeout(() => {
        message.widgetIds.forEach(value => {
          dispatch({ type: 'unHighlightWidget', id: value });
        });
      }, 3_000);
    },
  );

  const addWidget = (widgetId: string) => {
    if (!state.selectedDashboard) {
      dispatch({
        type: 'createDashboard',
        name: 'My Dashboard',
        id: `${crypto.randomUUID()}-${new Date().getTime()}`,
        sessionId,
      });
    }

    dispatch({
      type: 'addWidget',
      id: widgetId,
      save: true,
      sessionId,
    });
  };

  const removeWidgetById = (widget: string) => {
    dispatch({
      type: 'removeWidget',
      id: widget,
      sessionId,
    });
  };

  const createDashboard = (
    localDashboardName: string,
    layouts?: Layouts,
    preferences?: MNIDashboardWidgetPreferences,
  ) => {
    dispatch({
      type: 'createDashboard',
      name: localDashboardName,
      layouts: layouts,
      preferences: preferences,
      id: `${crypto.randomUUID()}-${new Date().getTime()}`,
      sessionId,
    });
    dispatch({ type: 'editCurrentDashboard' });
    setShowCreateDashboardModal(false);
  };
  const deleteSelectedDashboard = () => {
    dispatch({
      type: 'deleteCurrentDashboard',
      id: state.selectedDashboard,
      sessionId,
    });
  };

  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!state.currentBreakpoint && ref.current) {
      const currentBP = getBreakpointFromElement(ref.current);
      if (currentBP) {
        dispatch({ type: 'setBreakpoint', breakpoint: currentBP });
      }
    }
  }, [ref, state]);

  const confirmAddWidget = () => {
    return new Promise<boolean>(resolve => {
      setResolveAddWidgetPromise(() => resolve);
      setShowAddWidgetModal(true);
    });
  };

  const { registerWidgetNavigator, navigateOrWait } = useWidgetNavigatorRegistry();
  const mobileWidgetSelectorRef = useRef<((id: string, path?: string) => void) | null>(null);

  const latestRef = useRef({
    activeWidgets,
    selectedDashboard,
    sessionId,
    addWidget,
    removeWidgetById,
    confirmAddWidget,
    dispatch,
  });
  latestRef.current = {
    activeWidgets,
    selectedDashboard,
    sessionId,
    addWidget,
    removeWidgetById,
    confirmAddWidget,
    dispatch,
  };

  const widgetHost: WidgetHost = useMemo(
    () => ({
      get activeWidgetIds(): string[] {
        return latestRef.current.activeWidgets?.activeWidgets.map(value => value.id) || [];
      },
      removeWidgetById(id: string) {
        latestRef.current.removeWidgetById(id);
      },
      addWidgetById(id: string) {
        latestRef.current.addWidget(id);
        latestRef.current.dispatch({ type: 'editCurrentDashboard' });
      },
      get hostId(): string {
        return 'dashboard';
      },
      get currentViewId(): string {
        return latestRef.current.selectedDashboard?.id || 'default-view';
      },
      async setWidgetPath(id: string, path: string) {
        if (mobileWidgetSelectorRef.current) {
          mobileWidgetSelectorRef.current(id, path);
          return;
        }

        const { activeWidgets, confirmAddWidget, addWidget, dispatch } = latestRef.current;

        if (!activeWidgets?.activeWidgets.map(value => value.id).includes(id)) {
          const userConfirm = await confirmAddWidget();

          if (!userConfirm) {
            console.log('Add widget canceled by user');
            return;
          }

          addWidget(id);
          dispatch({ type: 'editCurrentDashboard' });
        }

        await navigateOrWait(id, path);
      },
      registerWidgetNavigator,
      registerActiveWidgetSelector(select: (id: string, path?: string) => void) {
        mobileWidgetSelectorRef.current = select;
        return () => {
          if (mobileWidgetSelectorRef.current === select) mobileWidgetSelectorRef.current = null;
        };
      },
      async getWidgetPreferences<T>(id: string): Promise<T> {
        let result: T = {} as T;
        const { selectedDashboard } = latestRef.current;

        if (selectedDashboard) {
          if (!selectedDashboard.widgetPreferences) selectedDashboard.widgetPreferences = {};

          if (selectedDashboard.widgetPreferences[id])
            result = selectedDashboard.widgetPreferences[id];
        }

        return result;
      },
      async saveWidgetPreferences<T>(id: string, t: T): Promise<T> {
        console.log('Saving widget preferences');
        const { selectedDashboard, sessionId, dispatch } = latestRef.current;

        if (selectedDashboard) {
          if (!selectedDashboard.widgetPreferences) selectedDashboard.widgetPreferences = {};

          selectedDashboard.widgetPreferences[id] = t;

          dispatch({ type: 'saveCurrentDashboard', sessionId });
        }

        return t;
      },
    }),

    [navigateOrWait, registerWidgetNavigator],
  );

  useEffect(() => {
    return registerInlineDashboardWidgetHandler((id, path) => {
      widgetHost.setWidgetPath(id, path ?? '');
    });
    // eslint-disable-next-line
  }, []);

  const handleResolve = (res: boolean) => {
    if (resolveAddWidgetPromise) {
      resolveAddWidgetPromise(res);
      setResolveAddWidgetPromise(null);
    }
    setShowAddWidgetModal(false);
  };

  const handleResolveAddWidgetSelectDashboard = (res: boolean, dashboard?: MNIDashboard) => {
    if (res && dashboard && addWidgetWidgetId) {
      dispatch({
        type: 'selectDashboard',
        dashboard: dashboard,
        sessionId,
      });
      setTimeout(() => {
        addWidget(addWidgetWidgetId);
      }, 500);
    }

    setAddWidgetWidgetId(undefined);
    setShowAddWidgetSelectDashboardModal(false);
  };

  return (
    <>
      <RequirePermissions allow={Permissions.NOTIFICATIONS}>
        <PushEnablePrompt />
      </RequirePermissions>
      <Context.Provider value={{ showMenu: false, localPreferences: context.localPreferences }}>
        <WidgetHostContext.Provider value={widgetHost}>
          <WidgetNavigationProvider>
            <RemoveWhenHiddenDiv className={'vh-100 d-block d-md-none'}>
              <MobileDashboardShell
                notificationSoundOn={notificationSoundOn}
                setNotificationSoundOn={setNotificationSoundOn}
              />
            </RemoveWhenHiddenDiv>

            <RemoveWhenHiddenDiv className="vh-100 dashboard-container d-none d-md-flex flex-column">
              <SearchProvider filterType={isAlphaFlashHost() ? 'event' : undefined}>
                <Navbar
                  selectedDashboard={selectedDashboard}
                  activeWidgets={activeWidgets}
                  notificationSoundOn={notificationSoundOn}
                  state={state}
                  addWidget={addWidget}
                  dispatch={dispatch}
                  setNotificationSoundOn={setNotificationSoundOn}
                  setShowCreateDashboardModal={setShowCreateDashboardModal}
                  setShowDeleteDashboardModal={setShowDeleteDashboardModal}
                  setShowMarkAsFavoriteDashboardModal={setShowMarkAsFavoriteModal}
                />
                <SearchResults />
                <div className={'d-flex overflow-auto h-100 vw-100 '}>
                  <div
                    className={`h-100 overflow-auto flex-grow-1 flex-shrink-1 dashboardPanelArea`}
                  >
                    {selectedDashboard && activeWidgets && (
                      <DashboardContent
                        dispatch={dispatch}
                        layouts={selectedDashboard.layouts}
                        editMode={state.editing}
                        activeWidgets={activeWidgets}
                        highlightedWidgets={state.highlightedWidgets}
                      />
                    )}

                    {state.loading && (
                      <div className={'container-fluid gx-2'}>
                        <div className={'row gx-2'}>
                          <div className={'col-md-4'}>
                            <div
                              className={' rounded-2 mt-2 dashboardPlaceholder'}
                              style={{ height: '20em' }}
                            ></div>
                            <div
                              className={'rounded-2 mt-2 dashboardPlaceholder'}
                              style={{ height: '20em' }}
                            ></div>
                          </div>
                          <div className={'col-md-4'}>
                            <div
                              className={'rounded-2 mt-2 dashboardPlaceholder'}
                              style={{ height: '10em' }}
                            ></div>
                            <div
                              className={'rounded-2 mt-2 dashboardPlaceholder'}
                              style={{ height: '40em' }}
                            ></div>
                          </div>
                          <div className={'col-md-4'}>
                            <div
                              className={'rounded-2 mt-2 dashboardPlaceholder'}
                              style={{ height: '30em' }}
                            ></div>
                            <div
                              className={'rounded-2 mt-2 dashboardPlaceholder'}
                              style={{ height: '10em' }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    )}
                    {!state.selectedDashboard && !state.loading && (
                      <div className={'container'}>
                        <div ref={ref} className={'row'}>
                          <div className={'col-12 text-center'}>
                            <div className={' m-5'}>
                              <h1 className={'mb-5'}>
                                Welcome to <span className={'fw-normal'}>My</span>MNI
                              </h1>
                              <div className={'row mb-5'}>
                                <div className={'col'}>
                                  <h3>
                                    <div className={'h1 mb-2'}>
                                      <FontAwesomeIcon icon={faSearch} />
                                    </div>
                                    Find
                                  </h3>
                                  <p>Find the information you need.</p>
                                </div>
                                <div className={'col'}>
                                  <h3>
                                    <h1>
                                      <FontAwesomeIcon icon={faRightLeft} />
                                    </h1>
                                    Organize
                                  </h3>
                                  <p>Create your custom experience.</p>
                                </div>
                                <div className={'col'}>
                                  <h3>
                                    <h1>
                                      <FontAwesomeIcon icon={faTowerCell} />
                                    </h1>
                                    Discover
                                  </h3>
                                  <p>Know what matters.</p>
                                </div>
                              </div>
                              <Button size={'lg'} onClick={() => setShowCreateDashboardModal(true)}>
                                <FontAwesomeIcon icon={faPlay} /> Get Started
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <RequirePermissions allow={Permissions.ALPHA_ACCESS_CHAT}>
                    <ChatDrawer fullWidth={false} />
                  </RequirePermissions>

                  <DashboardCreateModal
                    showCreateDashboardModal={showCreateDashboardModal}
                    setShowCreateDashboardModal={setShowCreateDashboardModal}
                    onCreate={createDashboard}
                  />

                  <DashboardCopyModal
                    showCopyDashboardModal={showCopyDashboardModal}
                    setShowCopyDashboardModal={b => {
                      setShowCopyDashboardModal(b);
                      if (!b) setCopyDashboard(undefined);
                    }}
                    dashboard={copyDashboard}
                    amountOfDashboards={state.dashboards?.length ?? 0}
                    onCopy={(dashboard: MNIDashboard) => {
                      createDashboard(
                        dashboard.name,
                        dashboard.layouts,
                        dashboard.widgetPreferences,
                      );
                      setShowCopyDashboardModal(false);
                      setCopyDashboard(undefined);
                    }}
                  />

                  <DashboardDeleteModal
                    showDeleteDashboardModal={showDeleteDashboardModal}
                    setShowDeleteDashboardModal={setShowDeleteDashboardModal}
                    onConfirm={deleteSelectedDashboard}
                  />

                  {selectedDashboard && (
                    <DashboardMarkAsFavoriteModal
                      showMarkAsFavoriteModal={showMarkAsFavoriteModal}
                      setShowMarkAsFavoriteModal={setShowMarkAsFavoriteModal}
                      isFavorite={selectedDashboard.favorite ?? false}
                      onConfirm={() => {
                        dispatch({
                          type: 'markAsFavorite',
                          dashboard: selectedDashboard,
                        });
                      }}
                    />
                  )}

                  <DashboardAddWidgetModal
                    showAddWidgetModal={showAddWidgetModal}
                    onResolve={handleResolve}
                  />

                  <DashboardAddWidgetSelectDashboardModal
                    showAddWidgetModal={showAddWidgetSelectDashboardModal}
                    dashboards={state.dashboards}
                    selectedDashboardValue={selectedDashboard}
                    onResolve={handleResolveAddWidgetSelectDashboard}
                  />
                </div>
              </SearchProvider>
            </RemoveWhenHiddenDiv>
          </WidgetNavigationProvider>
        </WidgetHostContext.Provider>
      </Context.Provider>
    </>
  );
}
